/**
 * 設定情報シートの印刷画面（運営者面 `/print/mail-setup?ids=...`）。
 *
 * ## なぜ運営者面にも要るのか
 *
 * 「メールソフトの設定が分からない」はサポートで最も多い問い合わせで、
 * そのとき契約者はコントロールパネルに辿り着けていないことが多い
 * （ログインできない・担当者が退職した・情シスに丸投げしている）。
 * 運営が**契約者に渡すのと同じ紙**をその場で出せると、電話口でポート番号を
 * 読み上げる必要が無くなる。読み上げは読み間違いを生み、そのまま
 * 二次問い合わせになる。
 *
 * ## 契約者面と同じ内容・同じ制約
 *
 *   - **パスワードは印字しない**（API が返さない）。
 *   - 値の出どころは API（`GET /ops/mailboxes/:id/setup-sheet`）だけ。
 *     ここでポート番号やホスト名を組み立てない。
 *   - 紙は常に白地・黒文字。運営コンソールのテーマには追随しない。
 *
 * ## レイアウトの外に置く
 *
 * `AppLayout`（サイドナビ・ヘッダ）の下に置くと、それらを消す `@media print`
 * を全画面ぶん書くことになる。印刷のことを知っているのはこの 1 枚だけにする。
 *
 * 複数件は 1 件ずつ取得する（`useQueries`）。運営者面のメールボックスは
 * **組織をまたぐ**ので、契約者面のような「組織 1 つぶんの一括取得」が
 * そのままでは使えない。サポートで出す枚数は多くて数枚なので、これで足りる。
 */
import * as React from 'react';
import { useQueries } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Alert, AlertDescription, Button } from '@mail-sv/ui';
import { ArrowLeft, Printer } from 'lucide-react';
import { api } from '../lib/api.js';
import { errorMessageOf } from '../components/states.js';

/** 1 行ぶんの接続設定。API（`services/setup-sheet.ts`）と同じ形。 */
interface SetupSheetConnection {
  purpose: string;
  protocol: 'IMAP' | 'POP3' | 'SMTP' | 'ManageSieve';
  host: string;
  port: number;
  encryption: 'SSL/TLS' | 'STARTTLS';
  recommended: boolean;
  note: string;
}

/**
 * メールアドレス 1 つぶんのシート。
 *
 * **型を `types/api.ts` に置かず、この画面に閉じている。** 印刷画面以外から
 * 参照されることが無く、共有の型ファイルへ足すと「どの画面がこの形に
 * 依存しているのか」が読めなくなるため。
 */
interface SetupSheet {
  mailboxId: string;
  address: string;
  displayName: string | null;
  domainName: string;
  host: string;
  username: string;
  connections: SetupSheetConnection[];
  webmailUrl: string | null;
  quotaBytes: number;
  usedBytes: number;
  autoconfigReady: boolean;
  issuedAt: string;
}

const BRAND = (import.meta.env.VITE_BRAND_NAME as string | undefined) ?? 'Aurora Host';

/** 容量表記。0 は無制限。契約者面の `formatQuota` と同じ言い回しにそろえる。 */
export function formatQuota(bytes: number): string {
  if (bytes === 0) return '無制限';
  const GiB = 1024 ** 3;
  if (bytes >= GiB) {
    const v = bytes / GiB;
    return `${v >= 100 ? Math.round(v) : Number(v.toFixed(1))} GB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
}

export function parseIds(raw: unknown): string[] {
  if (typeof raw !== 'string') return [];
  return [...new Set(raw.split(',').map((s) => s.trim()).filter((s) => s !== ''))];
}

export function PrintSetupSheetPage(): React.ReactElement {
  const search: { ids?: string } = useSearch({ strict: false });
  const ids = React.useMemo(() => parseIds(search.ids), [search.ids]);

  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['ops', 'setup-sheet', id] as const,
      queryFn: () => api.get<SetupSheet>(`/mailboxes/${encodeURIComponent(id)}/setup-sheet`),
      // 紙は「いま画面に出ている値」を刷るもの。開き直したら取り直す。
      staleTime: 0,
      retry: false,
    })),
  });

  const sheets = queries.map((q) => q.data).filter((s): s is SetupSheet => s !== undefined);
  const failed = queries.filter((q) => q.isError);
  const loading = queries.some((q) => q.isPending);

  return (
    <div className="min-h-screen bg-background p-4 print:bg-white print:p-0">
      <SetupSheetStyles />

      <div className="sheet-noprint mx-auto mb-6 flex max-w-[190mm] flex-wrap items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/mailboxes">
            <ArrowLeft className="size-4" aria-hidden />
            メールボックス一覧へ戻る
          </Link>
        </Button>
        <Button onClick={() => window.print()} disabled={sheets.length === 0}>
          <Printer className="size-4" aria-hidden />
          印刷する（PDF 保存もできます）
        </Button>
      </div>

      {ids.length === 0 ? (
        <div className="sheet-noprint mx-auto max-w-[190mm]">
          <Alert variant="warning">
            <AlertDescription>
              印刷するメールボックスが選ばれていません。メールボックス一覧の行メニューから
              「設定情報を印刷」を選んでください。
            </AlertDescription>
          </Alert>
        </div>
      ) : null}

      {loading ? (
        <p className="sheet-noprint mx-auto max-w-[190mm] text-sm text-muted-foreground">
          読み込んでいます…
        </p>
      ) : null}

      {/*
        取得できなかった件は**黙って落とさない**。束で渡す紙なので、
        枚数が合わないことに気づけないと事故になる。
      */}
      {failed.length > 0 ? (
        <div className="sheet-noprint mx-auto mb-4 max-w-[190mm]">
          <Alert variant="destructive">
            <AlertDescription>
              {failed.length} 件を取得できませんでした: {errorMessageOf(failed[0]?.error)}
            </AlertDescription>
          </Alert>
        </div>
      ) : null}

      {sheets.map((sheet) => (
        <SetupSheetCard key={sheet.mailboxId} sheet={sheet} />
      ))}
    </div>
  );
}

/**
 * シート専用のスタイル。
 *
 * **`<style>` は CSP で許されている**（`style-src 'self' 'unsafe-inline'`）。
 * インラインの `<script>` とは扱いが違う。
 *
 * **背景色は既定で印刷されない**ので、色に意味を持たせない
 * （枠線・太字・見出しの語で区別する）。ダークテーマをそのまま刷らせない
 * よう、紙面は常に白地・黒文字に固定する。
 */
function SetupSheetStyles(): React.ReactElement {
  return (
    <style>{`
.sheet-page {
  background: #ffffff;
  color: #111111;
  max-width: 190mm;
  margin: 0 auto 16mm;
  padding: 12mm 10mm;
  border: 1px solid #cccccc;
  font-family: system-ui, -apple-system, 'Segoe UI', 'Hiragino Sans', 'Noto Sans JP', sans-serif;
  font-size: 10.5pt;
  line-height: 1.6;
}
.sheet-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8mm; }
.sheet-title { font-size: 15pt; font-weight: 700; margin: 0 0 2mm; }
.sheet-brand { font-size: 9pt; color: #444444; text-align: right; white-space: nowrap; }
.sheet-address {
  font-family: ui-monospace, 'SFMono-Regular', Consolas, monospace;
  font-size: 13pt;
  font-weight: 700;
  word-break: break-all;
}
.sheet-section { margin-top: 6mm; }
.sheet-section h2 {
  font-size: 11pt;
  font-weight: 700;
  margin: 0 0 2mm;
  padding-bottom: 1mm;
  border-bottom: 2px solid #111111;
}
.sheet-kv { width: 100%; border-collapse: collapse; }
.sheet-kv th {
  text-align: left;
  width: 38mm;
  padding: 1.5mm 2mm 1.5mm 0;
  font-weight: 600;
  vertical-align: top;
  white-space: nowrap;
}
.sheet-kv td {
  padding: 1.5mm 0;
  vertical-align: top;
  word-break: break-all;
  font-family: ui-monospace, 'SFMono-Regular', Consolas, monospace;
}
.sheet-table { width: 100%; border-collapse: collapse; }
.sheet-table th, .sheet-table td {
  border: 1px solid #999999;
  padding: 1.5mm 2mm;
  text-align: left;
  vertical-align: top;
}
.sheet-table th { font-weight: 700; }
.sheet-table td.sheet-num { text-align: right; font-variant-numeric: tabular-nums; }
.sheet-mono { font-family: ui-monospace, 'SFMono-Regular', Consolas, monospace; word-break: break-all; }
.sheet-note { font-size: 9pt; color: #333333; margin: 2mm 0 0; }
.sheet-callout {
  margin-top: 3mm;
  border: 2px solid #111111;
  padding: 2.5mm 3mm;
  font-weight: 600;
}
.sheet-foot {
  margin-top: 6mm;
  padding-top: 2mm;
  border-top: 1px solid #999999;
  font-size: 8.5pt;
  color: #444444;
  display: flex;
  justify-content: space-between;
  gap: 6mm;
}
.sheet-advanced { margin-top: 3mm; }
.sheet-advanced summary { font-size: 9.5pt; cursor: pointer; }

@media print {
  .sheet-page { border: 0; margin: 0; padding: 0; max-width: none; page-break-after: always; }
  .sheet-page:last-of-type { page-break-after: auto; }
  .sheet-noprint { display: none !important; }
  .sheet-advanced > summary { display: none; }
  .sheet-advanced > *:not(summary) { display: block !important; }
  .sheet-table, .sheet-callout { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
}
@page { size: A4; margin: 14mm; }
`}</style>
  );
}

function SetupSheetCard({ sheet }: { sheet: SetupSheet }): React.ReactElement {
  const recommended = sheet.connections.filter((c) => c.recommended);
  const advanced = sheet.connections.filter((c) => !c.recommended);

  return (
    <article className="sheet-page">
      <header className="sheet-head">
        <div>
          <p className="sheet-title">メール設定情報</p>
          <p className="sheet-address">{sheet.address}</p>
          {sheet.displayName ? <p className="sheet-note">{sheet.displayName}</p> : null}
        </div>
        <div className="sheet-brand">
          <div>{BRAND}</div>
          <div>発行日: {new Date(sheet.issuedAt).toLocaleDateString('ja-JP')}</div>
        </div>
      </header>

      <div className="sheet-callout">
        ユーザー名（ログイン ID）は <span className="sheet-mono">{sheet.username}</span>{' '}
        です。@ より前だけではログインできません。
      </div>

      <section className="sheet-section">
        <h2>基本情報</h2>
        <table className="sheet-kv">
          <tbody>
            <tr>
              <th scope="row">メールアドレス</th>
              <td>{sheet.address}</td>
            </tr>
            <tr>
              <th scope="row">ユーザー名</th>
              <td>{sheet.username}</td>
            </tr>
            <tr>
              <th scope="row">パスワード</th>
              <td className="sheet-mono">管理者が設定した値（この用紙には記載しません）</td>
            </tr>
            <tr>
              <th scope="row">メールサーバー</th>
              <td>{sheet.host}</td>
            </tr>
            <tr>
              <th scope="row">保存できる容量</th>
              <td>{formatQuota(sheet.quotaBytes)}</td>
            </tr>
            {sheet.webmailUrl ? (
              <tr>
                <th scope="row">Webメール</th>
                <td>{sheet.webmailUrl}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      <section className="sheet-section">
        <h2>メールソフトに入力する値</h2>
        {recommended.length === 0 ? (
          <p className="sheet-note">
            このメールアドレスでは、メールソフトからの送受信が有効になっていません。
          </p>
        ) : (
          <table className="sheet-table">
            <thead>
              <tr>
                <th scope="col">用途</th>
                <th scope="col">種類</th>
                <th scope="col">サーバー名</th>
                <th scope="col">ポート</th>
                <th scope="col">暗号化</th>
              </tr>
            </thead>
            <tbody>
              {recommended.map((c) => (
                <tr key={`${c.protocol}-${c.port}`}>
                  <td>{c.purpose}</td>
                  <td>{c.protocol}</td>
                  <td className="sheet-mono">{c.host}</td>
                  <td className="sheet-num">{c.port}</td>
                  <td>{c.encryption}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {advanced.length > 0 ? (
          <details className="sheet-advanced">
            <summary>別のポートを使う場合（上級者向け）</summary>
            <table className="sheet-table" style={{ marginTop: '2mm' }}>
              <thead>
                <tr>
                  <th scope="col">用途</th>
                  <th scope="col">種類</th>
                  <th scope="col">ポート</th>
                  <th scope="col">暗号化</th>
                </tr>
              </thead>
              <tbody>
                {advanced.map((c) => (
                  <tr key={`${c.protocol}-${c.port}`}>
                    <td>{c.purpose}</td>
                    <td>{c.protocol}</td>
                    <td className="sheet-num">{c.port}</td>
                    <td>{c.encryption}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        ) : null}

        <p className="sheet-note">
          {sheet.autoconfigReady
            ? 'Outlook・Thunderbird では、メールアドレスとパスワードを入力するだけで上の値が自動で設定されます。うまくいかない場合だけ、上の値を手で入力してください。'
            : '自動設定には対応していません。上の値を手で入力してください。'}
        </p>
      </section>

      <footer className="sheet-foot">
        <span>パスワードはこの用紙に含まれません。忘れた場合は管理者が再設定します。</span>
        <span className="sheet-mono">{sheet.domainName}</span>
      </footer>
    </article>
  );
}
